import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { getHeroVideoUrl, getHeroVideoUrlSync } from "../utils/videoUtils";

const HeroVideoBackground = styled.video<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  opacity: 0.8;
  filter: brightness(0.7) contrast(1.2);
  background: #0a0a0f;
  transition: background 0.3s ease;
`;

const HeroVideoOverlay = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "linear-gradient(135deg, rgba(26, 26, 46, 0.3) 0%, rgba(22, 33, 62, 0.25) 50%, rgba(15, 52, 96, 0.2) 100%)"
      : "linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.2) 100%)"};
  z-index: 2;
`;

const HeroSection = styled.section<{ $isDarkMode: boolean }>`
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0a0a0f;
  color: white;
  text-align: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  transition: background 0.3s ease;
`;

const HeroContent = styled.div`
  max-width: 800px;
  z-index: 10;
  position: relative;
`;

const HeroTitle = styled.h1<{ $isDarkMode: boolean }>`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "linear-gradient(45deg, #e2e8f0, #cbd5e1)"
      : "linear-gradient(45deg, #fff, #f0f0f0)"};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "0 2px 4px rgba(0, 0, 0, 0.3)"
      : "0 1px 2px rgba(0, 0, 0, 0.1)"};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p<{ $isDarkMode: boolean }>`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: ${({ $isDarkMode }) => ($isDarkMode ? "0.95" : "0.9")};
  line-height: 1.6;
  color: ${({ $isDarkMode }) => ($isDarkMode ? "#e2e8f0" : "#ffffff")};
  text-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "0 1px 2px rgba(0, 0, 0, 0.2)"
      : "0 1px 1px rgba(0, 0, 0, 0.1)"};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(45deg, #ff6b6b, #ee5a24);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  margin-right: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.8);
  padding: 1rem 2rem;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
    transform: translateY(-2px);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const Footer = styled.footer`
  background: var(--surface-color);
  border-top: 1px solid var(--border-color);
  padding: 3rem 2rem 2rem;
  color: var(--text-color);
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const FooterSection = styled.div`
  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 0.5rem;
  }

  a {
    color: var(--text-secondary-color);
    text-decoration: none;
    transition: color 0.2s ease;
    font-size: 0.95rem;

    &:hover {
      color: var(--primary-color);
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid var(--border-color);
  padding-top: 2rem;
  text-align: center;
  color: var(--text-secondary-color);
  font-size: 0.9rem;

  p {
    margin: 0.5rem 0;
  }
`;

const FooterLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  background: var(--background-color);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.8rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  font-weight: 800;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--success-color)
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: var(--text-secondary-color);
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
  justify-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    padding: 0 1rem;
  }
`;

const FeatureCard = styled.div`
  background: var(--surface-color);
  padding: 2.5rem 2rem;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid var(--border-color);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--success-color)
    );
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
    border-color: var(--primary-color);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(
    45deg,
    rgba(102, 126, 234, 0.8),
    rgba(118, 75, 162, 0.8)
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2rem;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.2);
  transition: all 0.3s ease;

  ${FeatureCard}:hover & {
    transform: scale(1.1);
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.3);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 1rem;
  color: var(--text-color);
  text-align: center;
  font-weight: 700;
  line-height: 1.3;
`;

const FeatureDescription = styled.p`
  color: var(--text-secondary-color);
  line-height: 1.7;
  font-size: 1rem;
  text-align: center;
  margin: 0;
`;

const VisionSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 80%,
        rgba(255, 255, 255, 0.08) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(255, 255, 255, 0.06) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 40% 40%,
        rgba(255, 255, 255, 0.04) 0%,
        transparent 50%
      );
    animation: visionPulse 20s ease-in-out infinite;
  }

  @keyframes visionPulse {
    0%,
    100% {
      opacity: 0.3;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(1.05);
    }
  }
`;

const VisionContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const VisionTitle = styled.h2<{ $isDarkMode: boolean }>`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: ${({ $isDarkMode }) => ($isDarkMode ? "#ffffff" : "#1e293b")};
  text-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "0 2px 4px rgba(0, 0, 0, 0.3)"
      : "0 1px 2px rgba(0, 0, 0, 0.1)"};
  transition: all 0.3s ease;
`;

const VisionText = styled.p<{ $isDarkMode: boolean }>`
  font-size: 1.2rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  color: ${({ $isDarkMode }) => ($isDarkMode ? "#f1f5f9" : "#334155")};
  text-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "0 1px 2px rgba(0, 0, 0, 0.2)"
      : "0 1px 1px rgba(0, 0, 0, 0.05)"};
  transition: all 0.3s ease;
`;

const TechBackground = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "linear-gradient(135deg, rgba(18, 18, 18, 0.95) 0%, rgba(30, 30, 30, 0.9) 50%, rgba(45, 45, 45, 0.85) 100%)"
      : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(245, 245, 245, 0.9) 50%, rgba(235, 235, 235, 0.85) 100%)"};
  overflow: hidden;
  z-index: 1;
`;

const WavyLines = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${({ $isDarkMode }) =>
    $isDarkMode
      ? `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line1' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23a855f7;stop-opacity:0.25'/%3E%3Cstop offset='50%25' style='stop-color:%23ec4899;stop-opacity:0.2'/%3E%3Cstop offset='100%25' style='stop-color:%23f59e0b;stop-opacity:0.15'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M20,0 Q25,20 20,40 Q15,60 20,80 Q25,100 20,100' stroke='url(%23line1)' stroke-width='0.5' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='120' height='100' viewBox='0 0 120 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line2' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23ec4899;stop-opacity:0.2'/%3E%3Cstop offset='50%25' style='stop-color:%23f59e0b;stop-opacity:0.15'/%3E%3Cstop offset='100%25' style='stop-color:%23ef4444;stop-opacity:0.1'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M40,0 Q45,15 40,35 Q35,55 40,75 Q45,95 40,100' stroke='url(%23line2)' stroke-width='0.4' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='110' height='100' viewBox='0 0 110 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line3' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23f59e0b;stop-opacity:0.2'/%3E%3Cstop offset='50%25' style='stop-color:%23ef4444;stop-opacity:0.15'/%3E%3Cstop offset='100%25' style='stop-color:%23a855f7;stop-opacity:0.1'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M60,0 Q65,25 60,45 Q55,65 60,85 Q65,105 60,100' stroke='url(%23line3)' stroke-width='0.3' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='130' height='100' viewBox='0 0 130 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line4' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23ef4444;stop-opacity:0.15'/%3E%3Cstop offset='50%25' style='stop-color:%23a855f7;stop-opacity:0.1'/%3E%3Cstop offset='100%25' style='stop-color:%23ec4899;stop-opacity:0.08'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M80,0 Q85,30 80,50 Q75,70 80,90 Q85,110 80,100' stroke='url(%23line4)' stroke-width='0.35' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='140' height='100' viewBox='0 0 140 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line5' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23a855f7;stop-opacity:0.2'/%3E%3Cstop offset='50%25' style='stop-color:%23ec4899;stop-opacity:0.15'/%3E%3Cstop offset='100%25' style='stop-color:%23f59e0b;stop-opacity:0.1'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M100,0 Q105,35 100,55 Q95,75 100,95 Q105,115 100,100' stroke='url(%23line5)' stroke-width='0.4' fill='none'/%3E%3C/svg%3E")`
      : `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line1' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%233b82f6;stop-opacity:0.15'/%3E%3Cstop offset='50%25' style='stop-color:%23933aea;stop-opacity:0.1'/%3E%3Cstop offset='100%25' style='stop-color:%23ec4899;stop-opacity:0.08'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M20,0 Q25,20 20,40 Q15,60 20,80 Q25,100 20,100' stroke='url(%23line1)' stroke-width='0.5' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='120' height='100' viewBox='0 0 120 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line2' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23933aea;stop-opacity:0.12'/%3E%3Cstop offset='50%25' style='stop-color:%23ec4899;stop-opacity:0.08'/%3E%3Cstop offset='100%25' style='stop-color:%2334d399;stop-opacity:0.06'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M40,0 Q45,15 40,35 Q35,55 40,75 Q45,95 40,100' stroke='url(%23line2)' stroke-width='0.4' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='110' height='100' viewBox='0 0 110 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line3' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23ec4899;stop-opacity:0.1'/%3E%3Cstop offset='50%25' style='stop-color:%2334d399;stop-opacity:0.08'/%3E%3Cstop offset='100%25' style='stop-color:%23f59e0b;stop-opacity:0.06'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M60,0 Q65,25 60,45 Q55,65 60,85 Q65,105 60,100' stroke='url(%23line3)' stroke-width='0.3' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='130' height='100' viewBox='0 0 130 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line4' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%2334d399;stop-opacity:0.08'/%3E%3Cstop offset='50%25' style='stop-color:%23f59e0b;stop-opacity:0.06'/%3E%3Cstop offset='100%25' style='stop-color:%23ef4444;stop-opacity:0.04'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M80,0 Q85,30 80,50 Q75,70 80,90 Q85,110 80,100' stroke='url(%23line4)' stroke-width='0.35' fill='none'/%3E%3C/svg%3E"),
        url("data:image/svg+xml,%3Csvg width='130' height='100' viewBox='0 0 130 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='line5' x1='0%25' y1='0%25' x2='0%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23f59e0b;stop-opacity:0.09'/%3E%3Cstop offset='50%25' style='stop-color:%23ef4444;stop-opacity:0.07'/%3E%3Cstop offset='100%25' style='stop-color:%23a855f7;stop-opacity:0.05'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M100,0 Q105,35 100,55 Q95,75 100,95 Q105,115 100,100' stroke='url(%23line5)' stroke-width='0.4' fill='none'/%3E%3C/svg%3E")`};
  background-size: 100px 100px, 120px 100px, 110px 100px, 130px 100px,
    140px 100px;
  background-position: 0 0, 20px 0, 40px 0, 60px 0, 80px 0;
  animation: wavyFloat 35s ease-in-out infinite;
  transition: all 0.5s ease;

  @keyframes wavyFloat {
    0%,
    100% {
      transform: translateY(0px) scale(1);
    }
    50% {
      transform: translateY(-15px) scale(1.05);
    }
  }
`;

const ScatteredDots = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${({ $isDarkMode }) =>
    $isDarkMode
      ? `radial-gradient(circle at 15% 25%, rgba(168, 85, 247, 0.9) 1.5px, transparent 1.5px),
         radial-gradient(circle at 35% 45%, rgba(236, 72, 153, 0.8) 1px, transparent 1px),
         radial-gradient(circle at 55% 15%, rgba(245, 158, 11, 0.9) 2px, transparent 2px),
         radial-gradient(circle at 75% 65%, rgba(239, 68, 68, 0.7) 1.2px, transparent 1.2px),
         radial-gradient(circle at 25% 75%, rgba(168, 85, 247, 0.8) 1.8px, transparent 1.8px),
         radial-gradient(circle at 65% 85%, rgba(236, 72, 153, 0.6) 1px, transparent 1px),
         radial-gradient(circle at 85% 35%, rgba(245, 158, 11, 0.9) 1.5px, transparent 1.5px),
         radial-gradient(circle at 45% 95%, rgba(239, 68, 68, 0.7) 1.3px, transparent 1.3px),
         radial-gradient(circle at 95% 55%, rgba(168, 85, 247, 0.8) 1.7px, transparent 1.7px),
         radial-gradient(circle at 10% 85%, rgba(236, 72, 153, 0.6) 1px, transparent 1px),
         radial-gradient(circle at 30% 15%, rgba(245, 158, 11, 0.8) 1.4px, transparent 1.4px),
         radial-gradient(circle at 50% 65%, rgba(239, 68, 68, 0.7) 1.1px, transparent 1.1px),
         radial-gradient(circle at 70% 25%, rgba(168, 85, 247, 0.9) 1.6px, transparent 1.6px),
         radial-gradient(circle at 90% 75%, rgba(236, 72, 153, 0.6) 1.2px, transparent 1.2px),
         radial-gradient(circle at 20% 55%, rgba(245, 158, 11, 0.8) 1.9px, transparent 1.9px),
         radial-gradient(circle at 40% 35%, rgba(239, 68, 68, 0.7) 1.3px, transparent 1.3px),
         radial-gradient(circle at 60% 95%, rgba(168, 85, 247, 0.8) 1.5px, transparent 1.5px),
         radial-gradient(circle at 80% 15%, rgba(236, 72, 153, 0.6) 1.1px, transparent 1.1px)`
      : `radial-gradient(circle at 15% 25%, rgba(59, 130, 246, 0.9) 1.5px, transparent 1.5px),
         radial-gradient(circle at 35% 45%, rgba(147, 51, 234, 0.8) 1px, transparent 1px),
         radial-gradient(circle at 55% 15%, rgba(236, 72, 153, 0.9) 2px, transparent 2px),
         radial-gradient(circle at 75% 65%, rgba(34, 197, 94, 0.7) 1.2px, transparent 1.2px),
         radial-gradient(circle at 25% 75%, rgba(245, 158, 11, 0.8) 1.8px, transparent 1.8px),
         radial-gradient(circle at 65% 85%, rgba(239, 68, 68, 0.6) 1px, transparent 1px),
         radial-gradient(circle at 85% 35%, rgba(168, 85, 247, 0.9) 1.5px, transparent 1.5px),
         radial-gradient(circle at 45% 95%, rgba(16, 185, 129, 0.7) 1.3px, transparent 1.3px),
         radial-gradient(circle at 95% 55%, rgba(251, 146, 60, 0.8) 1.7px, transparent 1.7px),
         radial-gradient(circle at 10% 85%, rgba(99, 102, 241, 0.6) 1px, transparent 1px),
         radial-gradient(circle at 30% 15%, rgba(244, 63, 94, 0.8) 1.4px, transparent 1.4px),
         radial-gradient(circle at 50% 65%, rgba(14, 165, 233, 0.7) 1.1px, transparent 1.1px),
         radial-gradient(circle at 70% 25%, rgba(139, 92, 246, 0.9) 1.6px, transparent 1.6px),
         radial-gradient(circle at 90% 75%, rgba(6, 182, 212, 0.6) 1.2px, transparent 1.2px),
         radial-gradient(circle at 20% 55%, rgba(249, 115, 22, 0.8) 1.9px, transparent 1.9px),
         radial-gradient(circle at 40% 35%, rgba(220, 38, 127, 0.7) 1.3px, transparent 1.3px),
         radial-gradient(circle at 60% 95%, rgba(5, 150, 105, 0.8) 1.5px, transparent 1.5px),
         radial-gradient(circle at 80% 15%, rgba(217, 119, 6, 0.6) 1.1px, transparent 1.1px)`};
  background-size: 200px 200px, 180px 180px, 220px 220px, 160px 160px,
    240px 240px, 190px 190px, 210px 210px, 170px 170px, 230px 230px, 200px 200px,
    180px 180px, 220px 220px, 160px 160px, 240px 240px, 190px 190px, 210px 210px,
    170px 170px, 230px 230px;
  animation: waveMove 20s ease-in-out infinite;
  transition: all 0.5s ease;

  &:hover {
    animation-play-state: paused;
    transform: scale(1.1);
    filter: ${({ $isDarkMode }) =>
      $isDarkMode ? "brightness(1.4)" : "brightness(1.2)"};
  }

  @keyframes waveMove {
    0%,
    100% {
      transform: translateY(0px) translateX(0px) scale(1);
    }
    25% {
      transform: translateY(-30px) translateX(15px) scale(1.05);
    }
    50% {
      transform: translateY(-60px) translateX(-10px) scale(1.1);
    }
    75% {
      transform: translateY(-40px) translateX(20px) scale(1.05);
    }
  }
`;

const FloatingOrbs = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 30%;
  left: 20%;
  width: 60px;
  height: 60px;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "radial-gradient(circle, rgba(168, 85, 247, 0.6) 0%, rgba(168, 85, 247, 0.2) 50%, transparent 100%)"
      : "radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, rgba(236, 72, 153, 0.1) 50%, transparent 100%)"};
  border-radius: 50%;
  filter: blur(2px);
  animation: orbFloat1 15s ease-in-out infinite;
  cursor: pointer;
  transition: all 0.4s ease;

  &:hover {
    transform: scale(1.5) translateY(-30px);
    filter: blur(1px) brightness(1.3);
    box-shadow: ${({ $isDarkMode }) =>
      $isDarkMode
        ? "0 0 30px rgba(168, 85, 247, 0.8)"
        : "0 0 30px rgba(236, 72, 153, 0.6)"};
  }

  @keyframes orbFloat1 {
    0%,
    100% {
      transform: translateY(0px) scale(1) rotate(0deg);
      opacity: 0.4;
    }
    25% {
      transform: translateY(-25px) scale(1.2) rotate(90deg);
      opacity: 0.7;
    }
    50% {
      transform: translateY(-40px) scale(1.4) rotate(180deg);
      opacity: 1;
    }
    75% {
      transform: translateY(-20px) scale(1.1) rotate(270deg);
      opacity: 0.6;
    }
  }
`;

const FloatingOrbs2 = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 45%;
  right: 25%;
  width: 40px;
  height: 40px;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "radial-gradient(circle, rgba(239, 68, 68, 0.7) 0%, rgba(239, 68, 68, 0.25) 50%, transparent 100%)"
      : "radial-gradient(circle, rgba(245, 158, 11, 0.5) 0%, rgba(245, 158, 11, 0.15) 50%, transparent 100%)"};
  border-radius: 50%;
  filter: blur(1.5px);
  animation: orbFloat2 18s ease-in-out infinite;
  cursor: pointer;
  transition: all 0.4s ease;

  &:hover {
    transform: scale(1.8) translateY(-40px);
    filter: blur(0.5px) brightness(1.4);
    box-shadow: ${({ $isDarkMode }) =>
      $isDarkMode
        ? "0 0 25px rgba(239, 68, 68, 0.9)"
        : "0 0 25px rgba(245, 158, 11, 0.7)"};
  }

  @keyframes orbFloat2 {
    0%,
    100% {
      transform: translateY(0px) scale(1) rotate(0deg);
      opacity: 0.5;
    }
    33% {
      transform: translateY(-30px) scale(1.3) rotate(120deg);
      opacity: 0.8;
    }
    66% {
      transform: translateY(-50px) scale(1.6) rotate(240deg);
      opacity: 1;
    }
  }
`;

const FloatingOrbs3 = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 15%;
  right: 15%;
  width: 35px;
  height: 35px;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? "radial-gradient(circle, rgba(245, 158, 11, 0.6) 0%, rgba(245, 158, 11, 0.2) 50%, transparent 100%)"
      : "radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0.1) 50%, transparent 100%)"};
  border-radius: 50%;
  filter: blur(1px);
  animation: orbFloat3 22s ease-in-out infinite;
  cursor: pointer;
  transition: all 0.4s ease;

  &:hover {
    transform: scale(1.6) translateY(-25px);
    filter: blur(0.3px) brightness(1.5);
    box-shadow: ${({ $isDarkMode }) =>
      $isDarkMode
        ? "0 0 20px rgba(245, 158, 11, 0.9)"
        : "0 0 20px rgba(59, 130, 246, 0.8)"};
  }

  @keyframes orbFloat3 {
    0%,
    100% {
      transform: translateY(0px) scale(1) rotate(0deg);
      opacity: 0.4;
    }
    20% {
      transform: translateY(-20px) scale(1.1) rotate(72deg);
      opacity: 0.6;
    }
    40% {
      transform: translateY(-35px) scale(1.2) rotate(144deg);
      opacity: 0.8;
    }
    60% {
      transform: translateY(-45px) scale(1.3) rotate(216deg);
      opacity: 1;
    }
    80% {
      transform: translateY(-30px) scale(1.15) rotate(288deg);
      opacity: 0.7;
    }
  }
`;

const WaveRings = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 300px;
  height: 300px;
  border: 2px solid
    ${({ $isDarkMode }) =>
      $isDarkMode ? "rgba(168, 85, 247, 0.3)" : "rgba(59, 130, 246, 0.2)"};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: waveRing 25s ease-in-out infinite;
  cursor: pointer;
  transition: all 0.5s ease;

  &:hover {
    border-color: ${({ $isDarkMode }) =>
      $isDarkMode ? "rgba(168, 85, 247, 0.7)" : "rgba(59, 130, 246, 0.6)"};
    box-shadow: ${({ $isDarkMode }) =>
      $isDarkMode
        ? "0 0 40px rgba(168, 85, 247, 0.4)"
        : "0 0 40px rgba(59, 130, 246, 0.3)"};
    transform: translate(-50%, -50%) scale(1.2);
  }

  @keyframes waveRing {
    0%,
    100% {
      transform: translate(-50%, -50%) scale(0.8) rotate(0deg);
      opacity: 0.3;
    }
    25% {
      transform: translate(-50%, -50%) scale(1.1) rotate(90deg);
      opacity: 0.6;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.3) rotate(180deg);
      opacity: 0.8;
    }
    75% {
      transform: translate(-50%, -50%) scale(1.1) rotate(270deg);
      opacity: 0.6;
    }
  }
`;

const WaveRings2 = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 400px;
  height: 400px;
  border: 1px solid
    ${({ $isDarkMode }) =>
      $isDarkMode ? "rgba(236, 72, 153, 0.25)" : "rgba(147, 51, 234, 0.15)"};
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: waveRing2 30s ease-in-out infinite reverse;
  cursor: pointer;
  transition: all 0.5s ease;

  &:hover {
    border-color: ${({ $isDarkMode }) =>
      $isDarkMode ? "rgba(236, 72, 153, 0.6)" : "rgba(147, 51, 234, 0.5)"};
    box-shadow: ${({ $isDarkMode }) =>
      $isDarkMode
        ? "0 0 50px rgba(236, 72, 153, 0.3)"
        : "0 0 50px rgba(147, 51, 234, 0.2)"};
    transform: translate(-50%, -50%) scale(1.3);
  }

  @keyframes waveRing2 {
    0%,
    100% {
      transform: translate(-50%, -50%) scale(0.6) rotate(0deg);
      opacity: 0.2;
    }
    33% {
      transform: translate(-50%, -50%) scale(1.2) rotate(120deg);
      opacity: 0.5;
    }
    66% {
      transform: translate(-50%, -50%) scale(1.5) rotate(240deg);
      opacity: 0.7;
    }
  }
`;

const ParticleField = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${({ $isDarkMode }) =>
    $isDarkMode
      ? `radial-gradient(circle at 20% 30%, rgba(168, 85, 247, 0.4) 0%, transparent 50%),
         radial-gradient(circle at 80% 70%, rgba(236, 72, 153, 0.35) 0%, transparent 50%),
         radial-gradient(circle at 40% 60%, rgba(239, 68, 68, 0.3) 0%, transparent 50%)`
      : `radial-gradient(circle at 20% 30%, rgba(236, 72, 153, 0.3) 0%, transparent 50%),
         radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.25) 0%, transparent 50%),
         radial-gradient(circle at 40% 60%, rgba(147, 51, 234, 0.2) 0%, transparent 50%)`};
  animation: particleFloat 35s ease-in-out infinite;
  transition: all 0.3s ease;

  &:hover {
    animation-play-state: paused;
    filter: ${({ $isDarkMode }) =>
      $isDarkMode ? "brightness(1.5)" : "brightness(1.3)"};
  }

  @keyframes particleFloat {
    0%,
    100% {
      transform: translateY(0px) scale(1);
    }
    50% {
      transform: translateY(-25px) scale(1.1);
    }
  }
`;

const SubtleWaves = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  bottom: 0;
  left: -5%;
  width: 110%;
  height: 60%;
  background: ${({ $isDarkMode }) =>
    $isDarkMode
      ? `radial-gradient(ellipse at 30% 80%, rgba(168, 85, 247, 0.12) 0%, transparent 60%),
         radial-gradient(ellipse at 70% 70%, rgba(236, 72, 153, 0.1) 0%, transparent 50%),
         radial-gradient(ellipse at 50% 90%, rgba(239, 68, 68, 0.08) 0%, transparent 55%)`
      : `radial-gradient(ellipse at 30% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 60%),
         radial-gradient(ellipse at 70% 70%, rgba(147, 51, 234, 0.06) 0%, transparent 50%),
         radial-gradient(ellipse at 50% 90%, rgba(236, 72, 153, 0.05) 0%, transparent 55%)`};
  animation: waveDrift 40s ease-in-out infinite;
  transition: all 0.5s ease;

  @keyframes waveDrift {
    0%,
    100% {
      transform: translateY(0px) scale(1);
    }
    50% {
      transform: translateY(-20px) scale(1.05);
    }
  }
`;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signInWithGoogle } = useAuth();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [heroVideoUrl, setHeroVideoUrl] = useState<string>("");

  useEffect(() => {
    // Try to get video URL from Firebase Storage
    const loadVideoUrl = async () => {
      // First try async method (fetches from Firebase Storage - most reliable)
      try {
        const url = await getHeroVideoUrl();
        if (url) {
          setHeroVideoUrl(url);
          return;
        }
      } catch (error) {
        // Silently fall back to sync method
      }

      // Fallback: try synchronous method (constructs URL from config)
      const syncUrl = getHeroVideoUrlSync();
      if (syncUrl) {
        setHeroVideoUrl(syncUrl);
      }
    };

    loadVideoUrl();
  }, []);

  const handleGetStarted = async () => {
    if (user) {
      navigate("/create-map");
    } else {
      // Directly trigger Google authentication
      try {
        await signInWithGoogle();
        // After successful Google auth, redirect to dashboard
        navigate("/dashboard");
      } catch (error) {
        console.error("Google sign-in failed:", error);
        // If Google auth fails, fall back to signup page
        navigate("/signup");
      }
    }
  };

  const handleExploreMaps = () => {
    navigate("/explore");
  };

  return (
    <>
      <HeroSection $isDarkMode={isDarkMode}>
        {heroVideoUrl && (
          <HeroVideoBackground
            $isDarkMode={isDarkMode}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onError={(e) => {
              const video = e.currentTarget;
              if (video.error) {
                console.error("Hero video error:", {
                  code: video.error.code,
                  message: video.error.message,
                });
              }
            }}
          >
            <source src={heroVideoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </HeroVideoBackground>
        )}
        <HeroVideoOverlay $isDarkMode={isDarkMode} />
        <HeroContent>
          <HeroTitle $isDarkMode={isDarkMode} className="hero-title">
            {t("homepage.title")}
          </HeroTitle>
          <HeroSubtitle $isDarkMode={isDarkMode} className="hero-subtitle">
            {t("homepage.subtitle")}
          </HeroSubtitle>
          <ButtonContainer>
            <CTAButton onClick={handleGetStarted}>
              {user ? t("auth.createYourMap") : t("auth.joinNow")}
            </CTAButton>
            <SecondaryButton onClick={handleExploreMaps}>
              {t("homepage.exploreMaps")}
            </SecondaryButton>
          </ButtonContainer>
          {!user && (
            <p
              style={{
                textAlign: "center",
                marginTop: "1rem",
                opacity: 0.8,
                fontSize: "0.9rem",
              }}
            >
              {t("auth.signInOrCreateAccount")}
            </p>
          )}
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <Container>
          <SectionTitle className="section-title">
            {t("homepage.whyNewme")}
          </SectionTitle>
          <SectionSubtitle className="section-subtitle">
            {t("homepage.whyNewmeSubtitle")}
          </SectionSubtitle>
          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon>🗺️</FeatureIcon>
              <FeatureTitle>
                {t("homepage.features.interactiveMapping.title")}
              </FeatureTitle>
              <FeatureDescription>
                {t("homepage.features.interactiveMapping.description")}
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>👥</FeatureIcon>
              <FeatureTitle>
                {t("homepage.features.crowdSourcedDesign.title")}
              </FeatureTitle>
              <FeatureDescription>
                {t("homepage.features.crowdSourcedDesign.description")}
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>💬</FeatureIcon>
              <FeatureTitle>
                {t("homepage.features.communityDiscussion.title")}
              </FeatureTitle>
              <FeatureDescription>
                {t("homepage.features.communityDiscussion.description")}
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>🌍</FeatureIcon>
              <FeatureTitle>
                {t("homepage.features.selfDetermination.title")}
              </FeatureTitle>
              <FeatureDescription>
                {t("homepage.features.selfDetermination.description")}
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>📊</FeatureIcon>
              <FeatureTitle>
                {t("homepage.features.dataDriven.title")}
              </FeatureTitle>
              <FeatureDescription>
                {t("homepage.features.dataDriven.description")}
              </FeatureDescription>
            </FeatureCard>

            <FeatureCard>
              <FeatureIcon>🚀</FeatureIcon>
              <FeatureTitle>
                {t("homepage.features.futureReady.title")}
              </FeatureTitle>
              <FeatureDescription>
                {t("homepage.features.futureReady.description")}
              </FeatureDescription>
            </FeatureCard>
          </FeaturesGrid>
        </Container>
      </FeaturesSection>

      <VisionSection>
        <TechBackground $isDarkMode={isDarkMode}>
          <WavyLines $isDarkMode={isDarkMode} />
          <ScatteredDots $isDarkMode={isDarkMode} />
          <FloatingOrbs $isDarkMode={isDarkMode} />
          <FloatingOrbs2 $isDarkMode={isDarkMode} />
          <FloatingOrbs3 $isDarkMode={isDarkMode} />
          <WaveRings $isDarkMode={isDarkMode} />
          <WaveRings2 $isDarkMode={isDarkMode} />
          <ParticleField $isDarkMode={isDarkMode} />
          <SubtleWaves $isDarkMode={isDarkMode} />
        </TechBackground>
        <VisionContent>
          <VisionTitle $isDarkMode={isDarkMode} className="vision-title">
            {t("homepage.vision.title")}
          </VisionTitle>
          <VisionText $isDarkMode={isDarkMode} className="vision-text">
            {t("homepage.vision.text1")}
          </VisionText>
          <VisionText $isDarkMode={isDarkMode} className="vision-text">
            {t("homepage.vision.text2")}
          </VisionText>
        </VisionContent>
      </VisionSection>

      <Footer>
        <FooterContainer>
          <FooterGrid>
            <FooterSection>
              <FooterLogo>NewME.social</FooterLogo>
              <p
                style={{
                  color: "var(--text-secondary-color)",
                  lineHeight: "1.6",
                  marginBottom: "1rem",
                }}
              >
                {t("homepage.footer.description")}
              </p>
            </FooterSection>

            <FooterSection>
              <h3>{t("homepage.footer.platform")}</h3>
              <ul>
                <li>
                  <a href="/explore">{t("homepage.footer.exploreMaps")}</a>
                </li>
                <li>
                  <a href="/create">{t("homepage.footer.createMap")}</a>
                </li>
                <li>
                  <a href="/my-maps">{t("homepage.footer.myMaps")}</a>
                </li>
              </ul>
            </FooterSection>

            <FooterSection>
              <h3>{t("homepage.footer.support")}</h3>
              <ul>
                <li>
                  <a href="/contact">{t("homepage.footer.contact")}</a>
                </li>
                <li>
                  <a href="/faq">{t("homepage.footer.faq")}</a>
                </li>
                <li>
                  <a href="/changelog">{t("homepage.footer.changelog")}</a>
                </li>
              </ul>
            </FooterSection>

            <FooterSection>
              <h3>{t("homepage.footer.account")}</h3>
              <ul>
                <li>
                  <a href="/login">{t("homepage.footer.login")}</a>
                </li>
                <li>
                  <a href="/signup">{t("homepage.footer.signUp")}</a>
                </li>
                <li>
                  <a href="/profile">{t("homepage.footer.profile")}</a>
                </li>
                <li>
                  <a href="/settings">{t("homepage.footer.settings")}</a>
                </li>
              </ul>
            </FooterSection>
          </FooterGrid>

          <FooterBottom>
            <p className="footer-text">
              &copy; {new Date().getFullYear()} NewME.social.{" "}
              {t("homepage.footer.copyright")}
            </p>
            <p className="tagline">{t("homepage.footer.tagline")}</p>
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "center",
                gap: "2rem",
                flexWrap: "wrap",
              }}
            >
              <a
                href="/privacy"
                style={{
                  color: "var(--text-secondary-color)",
                  textDecoration: "none",
                }}
              >
                {t("homepage.footer.privacyPolicy")}
              </a>
              <a
                href="/terms"
                style={{
                  color: "var(--text-secondary-color)",
                  textDecoration: "none",
                }}
              >
                {t("homepage.footer.termsOfService")}
              </a>
            </div>
          </FooterBottom>
        </FooterContainer>
      </Footer>
    </>
  );
};

export default HomePage;
